import type { UnifiedFilter } from '../components/generic/FilterSection';

/** Prefix for sessionStorage keys — used by every module whose list uses {@link GenericListPage}. */
export const LIST_STATE_STORAGE_PREFIX = 'ums:listState:' as const;

/**
 * Stable key per list screen so filters/search do not collide between modules.
 * Pass `config.module.route.list` from the module config (e.g. `/clients`, `/crm-tasks`).
 */
export function getListStateStorageKey(listRoute: string): string {
  return `${LIST_STATE_STORAGE_PREFIX}${listRoute}`;
}

export interface GenericListPersistedState {
  searchTerm: string;
  unifiedFilters: Record<string, UnifiedFilter>;
  activeFilterTab: string;
  filtersVisible: boolean;
}

/** Restores search + advanced filters when returning from edit/detail (see persistGenericListState). */
export function loadPersistedGenericListState(
  storageKey: string,
  defaultFilterTab: string
): GenericListPersistedState | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(storageKey);
    if (!raw) return null;
    const p = JSON.parse(raw) as Record<string, unknown>;
    return {
      searchTerm: typeof p.searchTerm === 'string' ? p.searchTerm : '',
      unifiedFilters:
        p.unifiedFilters && typeof p.unifiedFilters === 'object' && !Array.isArray(p.unifiedFilters)
          ? (p.unifiedFilters as Record<string, UnifiedFilter>)
          : {},
      activeFilterTab: typeof p.activeFilterTab === 'string' ? p.activeFilterTab : defaultFilterTab,
      filtersVisible: typeof p.filtersVisible === 'boolean' ? p.filtersVisible : false,
    };
  } catch {
    return null;
  }
}

export function persistGenericListState(storageKey: string, state: GenericListPersistedState): void {
  try {
    sessionStorage.setItem(storageKey, JSON.stringify(state));
  } catch {
    // quota / private mode
  }
}

export function getCoaTreeCollapseStorageKey(listRoute: string, scopeId?: number | string): string {
  const base = `${getListStateStorageKey(listRoute)}:tree-collapse`;
  return scopeId == null ? base : `${base}:${scopeId}`;
}

/** Restores COA tree expand/collapse across refresh and in-tab navigation. */
export function loadPersistedTreeCollapsedIds(storageKey: string): Set<number> | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { collapsedIds?: unknown };
    if (!Array.isArray(parsed.collapsedIds)) return null;
    return new Set(parsed.collapsedIds.filter((id): id is number => typeof id === 'number'));
  } catch {
    return null;
  }
}

export function persistTreeCollapsedIds(storageKey: string, collapsedIds: Set<number>): void {
  try {
    sessionStorage.setItem(storageKey, JSON.stringify({ collapsedIds: Array.from(collapsedIds) }));
  } catch {
    // quota / private mode
  }
}
